import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star, MapPin, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { countryName, formatRange } from "@/lib/utils_gg";
import { posStyle } from "./CardImagePositioner";

const TYPE_CLASS = { allnighter: "gg-type-allnighter", alldayer: "gg-type-alldayer", live_show: "gg-type-live_show", record_fair: "gg-type-record_fair", weekender: "bg-primary text-primary-foreground" };

function FeaturedCard({ e, className = "" }) {
  const { t, lang } = useApp();
  return (
    <Link to={`/events/${e.slug}`} className={`group gg-card bg-background transition-colors duration-200 flex ${className}`} data-testid={`featured-card-${e.slug}`}>
      <div className="relative w-24 sm:w-28 shrink-0 overflow-hidden border-r border-foreground bg-muted">
        <img src={e.main_image} alt={e.name} loading="lazy" style={posStyle(e.image_position)} className="gg-flyer absolute inset-0 group-hover:scale-[1.02] transition-transform duration-300" />
      </div>
      <div className="p-2.5 sm:p-3 min-w-0 flex-1 flex flex-col gap-1 pr-9 sm:pr-10 relative">
        <ArrowRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/70 group-hover:text-primary group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          <span className="font-display text-primary font-semibold tracking-wide text-[11px] uppercase" data-testid={`featured-date-${e.slug}`}>{formatRange(e, lang)}</span>
          <span className={`eyebrow !text-[9px] px-1.5 py-0.5 shrink-0 ${TYPE_CLASS[e.event_type] || "bg-muted"}`}>{t(`types.${e.event_type}`)}</span>
        </div>
        <h3 className="font-display uppercase tracking-tight text-base sm:text-lg leading-tight group-hover:text-primary transition-colors truncate">{e.name}</h3>
        <div className="text-xs text-muted-foreground truncate inline-flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{e.city}, {countryName(e.country, lang)}</div>
        {e.genres?.length > 0 && <div className="flex flex-wrap gap-1 mt-0.5">{e.genres.slice(0, 3).map(g => <span key={g} className="gg-chip !py-0 !px-1.5 !text-[9px]">{t(`genres.${g}`)}</span>)}</div>}
      </div>
    </Link>
  );
}

export default function FeaturedRecommends() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const scroller = useRef(null);
  const [nav, setNav] = useState({ left: false, right: false });
  useEffect(() => { api.get("/events/featured").then(r => setItems(r.data.items || [])).catch(() => {}); }, []);
  const [index, setIndex] = useState(0);
  const updateNav = () => {
    const el = scroller.current; if (!el) return;
    setNav({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    const card = el.querySelector("a"); if (card) setIndex(Math.round(el.scrollLeft / (card.offsetWidth + 12)));
  };
  useEffect(() => { updateNav(); const el = scroller.current; if (!el) return; el.addEventListener("scroll", updateNav, { passive: true }); window.addEventListener("resize", updateNav); return () => { el.removeEventListener("scroll", updateNav); window.removeEventListener("resize", updateNav); }; }, [items]);
  if (!items.length) return null;
  const scrollBy = dir => { const el = scroller.current; if (!el) return; const card = el.querySelector("a"); el.scrollBy({ left: dir * ((card?.offsetWidth || 280) + 16), behavior: "smooth" }); };
  const n = items.length;
  return (
    <section className="mt-5 md:mt-6 mb-6 md:mb-8" data-testid="featured-recommends">
      <div className="gg-recommends border border-foreground px-3 py-1.5 md:px-4 flex items-center justify-between gap-3 mb-2">
        <h2 className="eyebrow !text-[11px] md:!text-xs text-foreground inline-flex items-center gap-2" data-testid="featured-heading"><Star className="w-3.5 h-3.5 fill-current text-[#111111]" />{t("soulRadarRecommends")}</h2>
        {n >= 2 && (
          <div className="flex items-center gap-1.5" data-testid="featured-nav">
            <span className="eyebrow !text-[10px] text-foreground/70 tabular-nums mr-1" data-testid="featured-counter">{index + 1}/{n}</span>
            <button type="button" onClick={() => scrollBy(-1)} disabled={!nav.left} className="gg-icon-btn !w-7 !h-7 !bg-background disabled:opacity-30" aria-label="previous" data-testid="featured-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" onClick={() => scrollBy(1)} disabled={!nav.right} className="gg-icon-btn !w-7 !h-7 !bg-background disabled:opacity-30" aria-label="next" data-testid="featured-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <div ref={scroller} className={`flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory ${n === 2 ? "md:grid md:grid-cols-2 md:overflow-visible" : ""}`} data-testid="featured-scroller">
        {items.map(e => <FeaturedCard key={e.event_id} e={e} className={`snap-start shrink-0 w-full sm:w-[440px] min-h-[104px] ${n === 1 ? "w-full sm:w-full" : n === 2 ? "md:w-auto" : "md:w-[46%]"}`} />)}
      </div>
    </section>
  );
}
